function formatNumber(value) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "—";
  }

  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatChange(value) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "—";
  }

  const formatted = Number(value).toFixed(2);
  return Number(value) > 0 ? `+${formatted}` : formatted;
}

function MetricTile({ label, value, subvalue, className = "" }) {
  if (value === null || value === undefined || value === "") return null;

  return (
    <div className={`finance-metric-tile ${className}`.trim()}>
      <span className="home-v3-kicker">{label}</span>
      <span className="finance-metric-value">{value}</span>
      {subvalue ? <span className="finance-metric-subvalue">{subvalue}</span> : null}
    </div>
  );
}

function DetailRow({ label, value }) {
  if (value === null || value === undefined || value === "") return null;

  return (
    <div className="finance-detail-row">
      <span className="home-v3-kicker">{label}</span>
      <span className="finance-detail-value">{value}</span>
    </div>
  );
}

export default function Metrics(props) {
  if (!props.instrument || !props.instrument.quoteData) return null;

  const quote = props.instrument.quoteData;
  const meta = props.instrument.metrics?.meta || {};
  const fiftyTwoWeekLow = Number(
    quote.fifty_two_week?.low || meta.fifty_two_week?.low
  );
  const fiftyTwoWeekHigh = Number(
    quote.fifty_two_week?.high || meta.fifty_two_week?.high
  );
  const current = Number(quote.c);
  const currentInRange =
    Number.isFinite(current) &&
    Number.isFinite(fiftyTwoWeekLow) &&
    Number.isFinite(fiftyTwoWeekHigh) &&
    fiftyTwoWeekHigh > fiftyTwoWeekLow;
  const rangePercent = currentInRange
    ? Math.min(
        100,
        Math.max(
          0,
          ((current - fiftyTwoWeekLow) / (fiftyTwoWeekHigh - fiftyTwoWeekLow)) * 100
        )
      )
    : null;
  const isNegativeChange = Number(quote.d) < 0;
  const changeText = formatChange(quote.d);
  const percentText =
    quote.dp === null || quote.dp === undefined || Number.isNaN(Number(quote.dp))
      ? "—"
      : `${Number(quote.dp) > 0 ? "+" : ""}${Number(quote.dp).toFixed(2)}%`;

  return (
    <div className="finance-metrics">
      <div className="finance-metrics-header">
        <div>
          <span className="home-v3-kicker">Market detail</span>
          <h3 className="finance-metrics-title">Snapshot.</h3>
        </div>
        <div className={`finance-change-chip ${isNegativeChange ? "is-down" : "is-up"}`}>
          <span className="finance-change-chip-label">Change</span>
          <span className="finance-change-chip-value">
            {changeText} ({percentText})
          </span>
        </div>
      </div>

      <div className="finance-metric-grid">
        <MetricTile
          label="Market price"
          value={`$${formatNumber(quote.c)}`}
          subvalue={quote.symbol || meta.symbol || "—"}
          className="finance-metric-tile-emphasis"
        />
        <MetricTile
          label="Previous close"
          value={`$${formatNumber(quote.pc)}`}
        />
        <MetricTile label="Open" value={`$${formatNumber(quote.o)}`} />
        <MetricTile label="Day range" value={`${formatNumber(quote.l)} - ${formatNumber(quote.h)}`} />
        <MetricTile label="Volume" value={quote.volume ? Number(quote.volume).toLocaleString() : "—"} />
        <MetricTile label="Exchange" value={quote.exchange || meta.exchange || "—"} />
      </div>

      <div className="finance-range-panel">
        <div className="finance-range-head">
          <span className="home-v3-kicker">52-week range</span>
          <span className="finance-range-values">
            <span>{`$${formatNumber(fiftyTwoWeekLow)}`}</span>
            <span>{`$${formatNumber(fiftyTwoWeekHigh)}`}</span>
          </span>
        </div>
        <div className="finance-range-track" aria-hidden="true">
          <span
            className="finance-range-fill"
            style={{ width: rangePercent === null ? "0%" : `${rangePercent}%` }}
          />
          {currentInRange ? (
            <span
              className="finance-range-marker"
              style={{ left: `${rangePercent}%` }}
            />
          ) : null}
        </div>
        <div className="finance-range-foot">
          <span>{`Current: $${formatNumber(quote.c)}`}</span>
          <span>{quote.name || meta.name || "—"}</span>
        </div>
      </div>

      <div className="finance-detail-grid">
        <DetailRow label="Name" value={quote.name || meta.name || "—"} />
        <DetailRow label="Currency" value={quote.currency || meta.currency || "—"} />
        <DetailRow label="Type" value={meta.type || "—"} />
        <DetailRow label="Symbol" value={quote.symbol || meta.symbol || "—"} />
      </div>
    </div>
  );
}
